import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState, type KeyboardEvent } from "react";
import type { BorderRadiusValue } from "../../../util/src/blueprint/types";
import dico from "../../../util/src/language/dico";
import { EDITOR_PICKER_POPOVER_Z_INDEX } from "./editorPickerPopoverZIndex";

export type BorderRadiusConfig = BorderRadiusValue;

type Corner = "topLeft" | "topRight" | "bottomRight" | "bottomLeft";
type RadiusScope = "all" | Corner;

type BorderRadiusPickerProps = {
  value: BorderRadiusConfig;
  disabled?: boolean;
  onChange: (nextConfig: BorderRadiusConfig) => void;
};

const DEFAULT_RADIUS_CONFIG: BorderRadiusConfig = {
  radius: {
    topLeft: "0px",
    topRight: "0px",
    bottomRight: "0px",
    bottomLeft: "0px",
  },
};

const RADIUS_SCOPES: RadiusScope[] = [
  "all",
  "topLeft",
  "topRight",
  "bottomLeft",
  "bottomRight",
];

const parsePx = (value: string, fallback = 0): number => {
  const parsed = Number.parseFloat(value.replace("px", "").trim());
  return Number.isFinite(parsed) ? Math.max(0, parsed) : fallback;
};

const BorderRadiusPicker: React.FC<BorderRadiusPickerProps> = ({
  value,
  disabled = false,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [scope, setScope] = useState<RadiusScope>("all");
  const labels =
    (
      dico().licensedPortal?.layoutEditor as {
        borderRadiusPicker?: Record<string, string>;
      }
    )?.borderRadiusPicker ?? ({} as Record<string, string>);

  const mergedValue = useMemo<BorderRadiusConfig>(
    () => ({
      radius: {
        topLeft: value?.radius?.topLeft ?? DEFAULT_RADIUS_CONFIG.radius.topLeft,
        topRight:
          value?.radius?.topRight ?? DEFAULT_RADIUS_CONFIG.radius.topRight,
        bottomRight:
          value?.radius?.bottomRight ??
          DEFAULT_RADIUS_CONFIG.radius.bottomRight,
        bottomLeft:
          value?.radius?.bottomLeft ?? DEFAULT_RADIUS_CONFIG.radius.bottomLeft,
      },
    }),
    [value],
  );

  const activeCorner = scope === "all" ? "topLeft" : scope;
  const activeRadius = parsePx(mergedValue.radius[activeCorner], 0);
  const popupOpen = Boolean(anchorEl);

  const updateRadius = (radiusPx: number) => {
    const nextValue = `${Math.max(0, Math.round(radiusPx))}px`;
    const nextConfig: BorderRadiusConfig = {
      radius: { ...mergedValue.radius },
    };
    if (scope === "all") {
      nextConfig.radius.topLeft = nextValue;
      nextConfig.radius.topRight = nextValue;
      nextConfig.radius.bottomRight = nextValue;
      nextConfig.radius.bottomLeft = nextValue;
    } else {
      nextConfig.radius[scope] = nextValue;
    }
    onChange(nextConfig);
  };

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={(event) => {
          if (!disabled) {
            setAnchorEl(event.currentTarget as HTMLElement);
          }
        }}
        onKeyDown={(event) => {
          if (!disabled && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            setAnchorEl(event.currentTarget as HTMLElement);
          }
        }}
        sx={{
          border: "1px solid #d8e0eb",
          borderRadius: "8px",
          backgroundColor: "#fbfcfe",
          p: "3px 8px",
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.55 : 1,
          "&:hover": {
            borderColor: disabled ? "#d8e0eb" : "#94a3b8",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: 30,
            backgroundColor: "#000",
            borderTopLeftRadius: mergedValue.radius.topLeft,
            borderTopRightRadius: mergedValue.radius.topRight,
            borderBottomRightRadius: mergedValue.radius.bottomRight,
            borderBottomLeftRadius: mergedValue.radius.bottomLeft,
          }}
        />
      </Box>

      <Popover
        open={popupOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          root: {
            sx: { zIndex: EDITOR_PICKER_POPOVER_Z_INDEX },
          },
        }}
      >
        <Box
          sx={{
            width: 430,
            background: "#fff",
            borderRadius: "12px",
            p: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: "#1f2937" }}
            >
              {labels.title ?? ""}
            </Typography>
            <Tooltip title={labels.reset ?? ""}>
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => onChange(DEFAULT_RADIUS_CONFIG)}
                >
                  <RestartAltIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={sectionLabelSx}>
              {labels.scopeLabel ?? ""}
            </Typography>
            <Box sx={toggleGroupSx}>
              {RADIUS_SCOPES.map((entry) => (
                <Box
                  key={entry}
                  role="button"
                  tabIndex={0}
                  onClick={() => setScope(entry)}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setScope(entry);
                    }
                  }}
                  sx={[chipSx, scope === entry ? chipActiveSx : null]}
                >
                  {labels[
                    `scope${entry.charAt(0).toUpperCase()}${entry.slice(1)}`
                  ] ?? ""}
                </Box>
              ))}
            </Box>
          </Box>

          <Box>
            <Typography sx={sectionLabelSx}>
              {labels.radiusLabel ?? ""}
            </Typography>
            <TextField
              size="small"
              type="number"
              fullWidth
              value={activeRadius}
              onChange={(event) =>
                updateRadius(Number(event.target.value) || 0)
              }
              sx={inputSx}
              slotProps={{ htmlInput: { min: 0, step: 1 } }}
            />
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const sectionLabelSx = {
  display: "block",
  fontSize: 12,
  color: "#64748b",
  mb: 0.75,
};

const toggleGroupSx = {
  display: "flex",
  gap: "5px",
  flexWrap: "nowrap",
};

const chipSx = {
  minWidth: 0,
  height: 28,
  padding: "0 10px",
  borderRadius: "6px",
  border: "1px solid #c7d2e0",
  background: "#ffffff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 12,
  color: "#334155",
  cursor: "pointer",
  userSelect: "none",
  "&:hover": {
    borderColor: "#94a3b8",
    background: "#f1f5f9",
  },
};

const chipActiveSx = {
  borderColor: "#4f9cff",
  color: "#0f3f79",
  fontWeight: 600,
  background: "rgba(79, 156, 255, 0.18)",
};

const inputSx = {
  "& .MuiInputBase-root": {
    height: 42,
    borderRadius: "6px",
    "& input": {
      fontSize: 14,
    },
  },
};

export default BorderRadiusPicker;
